import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";

/**
 * Live social-proof ticker on the landing page.
 *
 * Fetches the last N approved certifications and rotates through them one at a
 * time (fade every 4s). Renders nothing when there is no data — the landing
 * page must never show an empty "loading" state that shouts "no traction yet."
 *
 * Layout is a single-line horizontal strip that adapts to mobile by wrapping
 * to two rows. Every item is a clickable link to the property's public
 * certification page, which is where a real guest wants to end up.
 */

const relativeTime = (iso) => {
  if (!iso) return "just now";
  const then = new Date(iso).getTime();
  const min = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const d = Math.round(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
};

export default function RecentCertifiedTicker() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    api.get("/certifications/recent?limit=8")
      .then(({ data }) => { if (alive) setItems(data?.items || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const it = items[idx];

  return (
    <section
      aria-live="polite"
      data-testid="recent-certified-ticker"
      className="max-w-7xl mx-auto px-6 md:px-10 py-6"
    >
      <Link to={`/certification/${it.code}`} className="block group" data-testid="recent-certified-link">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 px-5 py-4">
            {/* Live pulse label */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-bold whitespace-nowrap">
                Just certified · {relativeTime(it.decided_at)}
              </span>
            </div>

            {/* Property name + location */}
            <div className="flex-1 min-w-0" data-testid="recent-certified-name">
              <div className="font-serif text-lg md:text-xl text-[#0A1128] truncate">
                {it.name}
              </div>
              {(it.city || it.country) && (
                <div className="text-xs text-slate-500 truncate">
                  {[it.city, it.country].filter(Boolean).join(", ")}
                </div>
              )}
            </div>

            {/* Score + arrow */}
            <div className="flex items-center gap-3 shrink-0">
              <div
                data-testid="recent-certified-score"
                className={`px-3 py-1.5 rounded-full font-semibold text-sm border ${
                  it.score >= 95
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-50 text-[#0A1128] border-slate-200"
                }`}
              >
                {it.score}
                <span className="text-slate-400 font-normal">/100</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 transition-colors" />
            </div>
          </div>

          {/* Rotation progress bar (only when >1 item) */}
          {items.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-100">
              <div
                className="h-full bg-emerald-500 transition-all duration-500 ease-linear"
                style={{ width: `${((idx + 1) / items.length) * 100}%` }}
              />
            </div>
          )}
        </div>
      </Link>
    </section>
  );
}
